import { SendHorizontal } from "lucide-react-native";
import { useCallback, useState } from "react";
import type { TextInputKeyPressEvent } from "react-native";
import { Box } from "../../ui/box";
import { Button, ButtonIcon } from "../../ui/button";
import { HStack } from "../../ui/hstack";
import { Textarea, TextareaInput } from "../../ui/textarea";
import { useBreakpoint } from "../../utils/mediaQuery";

const isShiftPressed = (nativeEvent: object): boolean =>
  "shiftKey" in nativeEvent && nativeEvent.shiftKey === true;

type Props = {
  onSend: (text: string) => Promise<boolean>;
  /** A turn is in flight; the backend would refuse a second one. */
  disabled?: boolean;
  placeholder?: string;
};

/**
 * The message box.
 *
 * Holds the draft until the send is *confirmed*. If the mutation fails — a network
 * blip, or the backend refusing a second turn while one is running — nothing was
 * persisted, so the text has to still be here. Clearing it optimistically would
 * lose whatever the user typed.
 */
const Composer = ({
  onSend,
  disabled = false,
  placeholder = "Escribe tu mensaje…",
}: Props) => {
  const [draft, setDraft] = useState("");
  // NativeWind's `focus:` modifier never fires here — focus lands on the inner
  // `TextInput`, not the `Textarea` wrapper — so the highlight has to be tracked
  // by hand and applied down to the wrapper explicitly.
  const [isFocused, setIsFocused] = useState(false);
  // A local lock, distinct from `disabled`: `disabled` only flips true once the
  // send mutation round-trips, so holding Enter (OS key-repeat) or a double-tap
  // fires a second `submit()` before the first has even reached the network.
  const [isSending, setIsSending] = useState(false);
  // On a phone the send button sits beside a one-line field instead of under it:
  // the stacked layout took a fifth of the screen before anything was typed.
  const { isBelow } = useBreakpoint();
  const compact = isBelow("md");

  const submit = useCallback(async () => {
    const text = draft.trim();
    if (!text || disabled || isSending) return;
    setIsSending(true);
    try {
      const sent = await onSend(text);
      // Only now is the message durable. On failure the draft stays put.
      if (sent) setDraft("");
    } finally {
      setIsSending(false);
    }
  }, [draft, disabled, isSending, onSend]);

  const handleKeyPress = useCallback(
    (event: TextInputKeyPressEvent) => {
      if (event.nativeEvent.key !== "Enter") return;
      // React Native's key-press event only declares `key`, but on web the real
      // DOM event carries the modifiers too. Shift+Enter must insert a newline
      // rather than send, so read it defensively instead of asserting a type that
      // does not hold on native.
      if (isShiftPressed(event.nativeEvent)) return;
      event.preventDefault();
      void submit();
    },
    [submit],
  );

  const canSend = draft.trim().length > 0 && !disabled && !isSending;

  // The card's border: a stronger brand ring while the field itself is focused, a
  // plain outline otherwise. Never a heavier, "harsh" border — this is a floating
  // input, not a boxed-off panel.
  const borderClass = isFocused ? "border-brand-200" : "border-outline-100";

  return (
    // No fill of its own, no top border — this blends straight into the chat
    // background behind it, so the input reads as floating on the page rather
    // than sitting in a separate strip.
    <Box className={`bg-background-50 ${compact ? "pt-2" : "px-3 pb-3 pt-1"}`}>
      {/* One element in both layouts, only its direction changes: swapping the
          component on a rotation would remount the field and close the keyboard. */}
      <Box
        className={`rounded-3xl border bg-background-0 shadow-soft-1 ${borderClass} ${
          compact
            ? "flex-row items-end gap-2 py-1.5 pl-4 pr-1.5"
            : "flex-col gap-1 px-5 py-2.5"
        } ${disabled ? "opacity-60" : ""}`}
      >
        {/* Half the Textarea base's h-[100px] on desktop (the founder's verdict was
            that the full-height field ate too much of the chat column), one line on a
            phone. Shift+Enter still grows nothing — long drafts scroll inside. */}
        <Textarea
          size="md"
          isDisabled={disabled}
          className={`${compact ? "h-9 flex-1 self-center" : "h-[50px]"} border-0 rounded-none px-0 data-[focus=true]:web:ring-0 data-[focus=true]:web:outline-none data-[disabled=true]:bg-transparent`}
        >
          <TextareaInput
            placeholder={placeholder}
            value={draft}
            onChangeText={setDraft}
            onKeyPress={handleKeyPress}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            accessibilityLabel={placeholder}
            className="p-0"
          />
        </Textarea>
        {/* Send lives inside the card: bottom-right under the field on desktop, same
            layout as Claude's composer; beside it on a phone. */}
        <HStack className="justify-end">
          <Button
            action="brand"
            variant="solid"
            size="circle-sm"
            rounded="full"
            isDisabled={!canSend}
            onPress={submit}
          >
            <ButtonIcon as={SendHorizontal} />
          </Button>
        </HStack>
      </Box>
    </Box>
  );
};

export default Composer;
